import React, { useState } from 'react';
import axios from 'axios';
import emailjs from 'emailjs-com';
import {
    Button,
    AppBar,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    Snackbar,
    TextField,
    Toolbar,
    Typography
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import './sgpform.css';

const REGISTRATION_URL = 'https://satprerna.org/satprerna_backend/home/govibes2026_register.php';
const PAYMENT_STATUS_URL = 'https://satprerna.org/satprerna_backend/home/govibes2026_update_status.php';
const PAYMENT_AMOUNT = 100;
const RAZORPAY_KEY = 'rzp_live_bH8zSPYWzsshms';

const useStyles = makeStyles((theme) => ({
    appBar: {
        position: 'relative',
        boxShadow: 'none',
        backgroundColor: 'transparent'
    },
    title: {
        flex: 1
    },
    close: {
        color: 'white',
        backgroundColor: '#9a0808',
        position: 'fixed',
        top: '20px',
        right: '20px',
        '&:hover': {
            color: 'white',
            backgroundColor: '#9a0808'
        }
    },
    form: {
        maxWidth: 800,
        margin: '0 auto'
    },
    field: {
        marginBottom: theme.spacing(3)
    },
    ageGroup: {
        marginBottom: theme.spacing(3)
    },
    submit: {
        marginTop: theme.spacing(2)
    },
    instructionsTitle: {
        color: '#9a0808',
        fontWeight: 700,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(1)
    },
    instructionsList: {
        paddingLeft: theme.spacing(3),
        margin: 0,
        '& li': {
            paddingLeft: theme.spacing(0.5),
            marginBottom: theme.spacing(2),
            lineHeight: 1.8
        },
        '& li:last-child': {
            marginBottom: 0
        }
    },
    instructionSubpoint: {
        display: 'block',
        marginTop: theme.spacing(0.5)
    }
}));

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function loadRazorpay() {
    if (window.Razorpay) return Promise.resolve(true);

    return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(Boolean(window.Razorpay));
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

export default function GoVibes26() {
    const classes = useStyles();
    const [hasReadInstructions, setHasReadInstructions] = useState(false);
    const [fullName, setFullName] = useState('');
    const [mobile, setMobile] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [ageGroup, setAgeGroup] = useState('');
    const [city, setCity] = useState('');
    const [registrationId, setRegistrationId] = useState('');
    const [pendingPaymentId, setPendingPaymentId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notice, setNotice] = useState({ open: false, severity: 'info', text: '' });

    const showNotice = (severity, text) => setNotice({ open: true, severity, text });

    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const validateMobile = (value) => /^[0-9]{10}$/.test(value);

    const clearForm = () => {
        setFullName('');
        setMobile('');
        setMobileError('');
        setEmail('');
        setEmailError('');
        setAgeGroup('');
        setCity('');
        setRegistrationId('');
        setPendingPaymentId('');
        setHasReadInstructions(false);
    };

    const confirmPayment = async (id, paymentId) => {
        try {
            const formData = new FormData();
            formData.append('id', id);
            formData.append('razorpay_payment_id', paymentId);
            await axios.post(PAYMENT_STATUS_URL, formData);
        } catch (error) {
            setPendingPaymentId(paymentId);
            showNotice('error', `Payment was received, but registration confirmation failed. Retry confirmation or contact us with payment ID ${paymentId}.`);
            setIsSubmitting(false);
            return;
        }

        clearForm();
        try {
            await emailjs.send(
                'service_8m18sys',
                'template_2gziwjf',
                { to_email: email },
                'user_sY1m8Ki5Oafoc32NNK9TG'
            );
            showNotice('success', 'Registration and payment completed successfully! A confirmation email has been sent.');
        } catch (error) {
            showNotice('warning', 'Payment completed successfully, but the confirmation email could not be sent.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openPayment = async (id) => {
        const razorpayLoaded = await loadRazorpay();
        if (!razorpayLoaded) {
            showNotice('error', 'Unable to load payment. Please try again.');
            setIsSubmitting(false);
            return;
        }

        const options = {
            key: RAZORPAY_KEY,
            amount: PAYMENT_AMOUNT * 100,
            currency: 'INR',
            name: 'Satprerna Trust',
            description: 'GO-VIBES 2026 Registration',
            handler: (response) => confirmPayment(id, response.razorpay_payment_id),
            modal: {
                ondismiss: () => {
                    showNotice('warning', 'Payment was not completed. You can try again using the same registration.');
                    setIsSubmitting(false);
                }
            },
            prefill: {
                name: fullName,
                email,
                contact: mobile
            },
            notes: {
                city,
                registration_id: id
            },
            theme: {
                color: '#fa6400'
            }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', () => {
            showNotice('error', 'Payment failed. Please try again.');
            setIsSubmitting(false);
        });
        razorpay.open();
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;
        if (!hasReadInstructions) {
            showNotice('error', 'Please read and accept the instructions before continuing.');
            return;
        }
        if (!validateMobile(mobile)) {
            setMobileError('Enter exactly 10 digits / कृपया 10 अंकों का मोबाइल नंबर दर्ज करें');
            showNotice('error', 'Please enter a valid 10-digit mobile number.');
            return;
        }
        if (!validateEmail(email.trim())) {
            setEmailError('Enter a valid email address / कृपया सही ईमेल दर्ज करें');
            showNotice('error', 'Please enter a valid email address.');
            return;
        }
        setIsSubmitting(true);

        if (registrationId) {
            if (pendingPaymentId) {
                await confirmPayment(registrationId, pendingPaymentId);
            } else {
                await openPayment(registrationId);
            }
            return;
        }

        try {
            const formData = new FormData();
            formData.append('fullName', fullName.trim());
            formData.append('mobile', mobile.trim());
            formData.append('email', email.trim());
            formData.append('ageGroup', ageGroup);
            formData.append('city', city.trim());
            const response = await axios.post(REGISTRATION_URL, formData);
            if (!response.data || typeof response.data !== 'object' || !response.data.id) {
                throw new Error('registration_endpoint_unavailable');
            }
            setRegistrationId(response.data.id);
            await openPayment(response.data.id);
        } catch (error) {
            const responseData = error.response && error.response.data;
            let errorMessage = 'Unable to submit your registration. Please try again.';
            if (error.message === 'registration_endpoint_unavailable' || typeof responseData === 'string') {
                errorMessage = 'The GoVibes registration PHP endpoint is not available at the configured URL. Please deploy the backend PHP file and run its SQL schema.';
            } else if (responseData && responseData.error === 'registration_failed') {
                errorMessage = 'Registration could not be saved. Please ensure the GoVibes database table has been created.';
            } else if (responseData && responseData.error === 'invalid_registration_details') {
                errorMessage = 'Please check the registration details and enter a valid 10-digit mobile number and email address.';
            } else if (error.request && !error.response) {
                errorMessage = 'Could not reach the registration server. Please check the backend endpoint and try again.';
            }
            showNotice('error', errorMessage);
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <div className="form-header">
                <AppBar className={classes.appBar}>
                    <Toolbar>
                        <Typography className={classes.title} />
                        <Link to="/">
                            <IconButton className={classes.close} edge="start" color="inherit" aria-label="close">
                                <CloseIcon />
                            </IconButton>
                        </Link>
                    </Toolbar>
                </AppBar>
                <svg className="wavy-divider" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none" width="100%" height="100">
                    <path d="M 1000 299 l 2 -279 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -232 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z" />
                    <path d="M 1000 286 l 2 -252 c -157 -43 -302 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -145.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 10.61 v 22.39 z" />
                    <path d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z" />
                </svg>
            </div>
            <div className="form-wrapper">
                <h1 className="t1">GO-VIBES 2026</h1>
                <div className={classes.form}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6" className={classes.instructionsTitle}>
                                    आवश्यक दिशा-निर्देश एवं सूचना
                                </Typography>
                                <ol className={classes.instructionsList}>
                                    <li>
                                        <strong>ड्रेस कोड:</strong> सभी गौ भक्तों एवं स्वयंसेवकों के लिए सफेद वस्त्र पहनना अनिवार्य है। पुरुषों के लिए सफेद कुर्ता-पायजामा अथवा शर्ट-पैंट तथा महिलाओं के लिए सफेद साड़ी अथवा सलवार-सूट निर्धारित है।
                                    </li>
                                    <li>
                                        <strong>अनिवार्य पंजीकरण:</strong> उत्सव में सम्मिलित होने के लिए प्रत्येक व्यक्ति का पंजीकरण अनिवार्य है। पंजीकरण शुल्क ₹100 प्रति व्यक्ति निर्धारित है।
                                    </li>
                                    <li>
                                        <strong>गौ-ग्रास सेवा:</strong> गोपाष्टमी के पावन अवसर पर गौशाला में गौ-ग्रास की व्यवस्था उपलब्ध रहेगी। अतः सभी गौ भक्तों से निवेदन है कि घर से खाद्य सामग्री बनाकर न लाएँ।
                                    </li>
                                    <li>
                                        <strong>कार्यक्रम का समय:</strong>
                                        <span className={classes.instructionSubpoint}>14 नवंबर 2026: सायं 7:00 बजे से।</span>
                                        <span className={classes.instructionSubpoint}>15 नवंबर 2026: प्रातः 9:00 बजे से।</span>
                                    </li>
                                    <li>
                                        <strong>रात्रि विश्राम की व्यवस्था:</strong> 14 नवंबर 2026 को रात्रि विश्राम करने के इच्छुक गौ भक्तों से अनुरोध है कि वे अपनी पूर्व सूचना अनिवार्य रूप से दें, ताकि उनके ठहरने की समुचित व्यवस्था की जा सके।
                                    </li>
                                </ol>
                                <br/>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            required
                                            color="primary"
                                            checked={hasReadInstructions}
                                            disabled={Boolean(pendingPaymentId)}
                                            onChange={(event) => {
                                                setHasReadInstructions(event.target.checked);
                                                setRegistrationId('');
                                                setPendingPaymentId('');
                                            }}
                                        />
                                    }
                                    label="I have read and agree to the instructions / मैंने ऊपर दिए गए दिशा-निर्देश पढ़ लिए हैं और उन्हें स्वीकार करता/करती हूँ।"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.field}
                                    fullWidth
                                    required
                                    disabled={Boolean(pendingPaymentId)}
                                    label="Full Name / पूरा नाम"
                                    autoComplete="name"
                                    value={fullName}
                                    onChange={(event) => {
                                        setFullName(event.target.value);
                                        setRegistrationId('');
                                        setPendingPaymentId('');
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.field}
                                    fullWidth
                                    required
                                    disabled={Boolean(pendingPaymentId)}
                                    label="WhatsApp Number / व्हाट्सएप नंबर"
                                    type="tel"
                                    inputProps={{ inputMode: 'numeric', maxLength: 10, pattern: '[0-9]{10}' }}
                                    autoComplete="tel"
                                    value={mobile}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        if (!/^[0-9]*$/.test(value) || value.length > 10) return;
                                        setMobile(value);
                                        setMobileError(value.length === 10 ? '' : value ? 'Enter exactly 10 digits / कृपया 10 अंकों का मोबाइल नंबर दर्ज करें' : '');
                                        setRegistrationId('');
                                        setPendingPaymentId('');
                                    }}
                                    onBlur={() => {
                                        if (mobile && !validateMobile(mobile)) {
                                            setMobileError('Enter exactly 10 digits / कृपया 10 अंकों का मोबाइल नंबर दर्ज करें');
                                        }
                                    }}
                                    error={Boolean(mobileError)}
                                    helperText={mobileError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.field}
                                    fullWidth
                                    required
                                    disabled={Boolean(pendingPaymentId)}
                                    label="Email / ईमेल"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(event) => {
                                        setEmail(event.target.value);
                                        setEmailError(event.target.value && !validateEmail(event.target.value.trim()) ? 'Enter a valid email address / कृपया सही ईमेल दर्ज करें' : '');
                                        setRegistrationId('');
                                        setPendingPaymentId('');
                                    }}
                                    onBlur={() => {
                                        if (email && !validateEmail(email.trim())) {
                                            setEmailError('Enter a valid email address / कृपया सही ईमेल दर्ज करें');
                                        }
                                    }}
                                    error={Boolean(emailError)}
                                    helperText={emailError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl component="fieldset" required disabled={Boolean(pendingPaymentId)} className={classes.ageGroup}>
                                    <FormLabel component="legend">Age Group / आयु वर्ग</FormLabel>
                                    <RadioGroup
                                        name="ageGroup"
                                        value={ageGroup}
                                        onChange={(event) => {
                                            setAgeGroup(event.target.value);
                                            setRegistrationId('');
                                            setPendingPaymentId('');
                                        }}
                                    >
                                        <FormControlLabel value="Below 18 Years" control={<Radio required color="primary" />} label="Below 18 Years / 18 वर्ष से कम" />
                                        <FormControlLabel value="18–25 Years" control={<Radio required color="primary" />} label="18–25 Years / 18–25 वर्ष" />
                                        <FormControlLabel value="26–35 Years" control={<Radio required color="primary" />} label="26–35 Years / 26–35 वर्ष" />
                                        <FormControlLabel value="Above 35 Years" control={<Radio required color="primary" />} label="Above 35 Years / 35 वर्ष से अधिक" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    className={classes.field}
                                    fullWidth
                                    required
                                    disabled={Boolean(pendingPaymentId)}
                                    label="City / Location / शहर / स्थान"
                                    autoComplete="address-level2"
                                    value={city}
                                    onChange={(event) => {
                                        setCity(event.target.value);
                                        setRegistrationId('');
                                        setPendingPaymentId('');
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6" align="center">आगच्छतु! अनुभवतु! विस्मयम्!</Typography>
                                <Typography variant="body1" align="center" className={classes.submit}>Registration Fee: ₹{PAYMENT_AMOUNT}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    className={classes.submit}
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Please wait...' : pendingPaymentId ? 'Retry Payment Confirmation' : registrationId ? 'Retry Payment — ₹100' : 'Register & Pay ₹100'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
            <Snackbar
                open={notice.open}
                autoHideDuration={6000}
                onClose={() => setNotice((current) => ({ ...current, open: false }))}
            >
                <Alert severity={notice.severity} onClose={() => setNotice((current) => ({ ...current, open: false }))}>
                    {notice.text}
                </Alert>
            </Snackbar>
        </div>
    );
}
